<template>
  <div class="root" v-bind:class="{ brand: isBrand() }">
    <div class="ar-container" v-show="isAR">
      <a class="ar-back" @click.prevent.stop="closeAR()">{{ $t('戻る') }}</a>
      <model-viewer
        ref="ModelViewer"
        v-if="userAgent.includes('iOS')"
        poster=""
        v-bind:src="glbUrl"
        v-bind:ios-src="usdzUrl"
        ar reveal="auto"
        auto-rotate
        camera-controls
        alt="3D model"
        class="ar"
      ></model-viewer>
      <model-viewer
        ref="ModelViewer"
        v-if="userAgent.includes('Android')"
        poster=""
        v-bind:src="glbUrl"
        ar reveal="auto"
        auto-rotate
        camera-controls
        alt="3D model"
        class="ar"
      ></model-viewer>
      <LoadingIndicator v-show="isLoading" />
    </div>
    <div v-show="!isAR">
      <LeftMenuCard
        v-if="mode === Modes.TOOLPATH_ONLY"
        :login="login"
        :name="factory_profile.name"
        :projectId="projectId"
        :totalPrice="totalPrice"
        :detailPrices="detailPrices"
        :factory_logo="factory_logo"
        @requireLogin="onRequireLogin"
        @logout="onLogout"
        @setPrice="onSetPrice"
        @calculate="showCalculatePopup"
      />
      <StartModal
        v-if="isModal"
        @closeModal="onCloseModal"
      />
      <div v-show="!isBrand()" class="icon pc" v-html="svg.logo"></div>
      <Meatball @open="isOpenMobileMenu = $event" />
      <LeftsideMenu
        v-show="!isBrand() && isOpenSideBar && mode !== Modes.TOOLPATH_ONLY"
        :userAgent="userAgent"
        :isAREnabled="true"
        :generatable="exportComponents().length > 0"
        :mode="mode"
        @showtemplate="onShowTemplate"
        @uploaddesign="onUploadDesign"
        @togglesbp="onToggleSbp"
        @iosAR="oniOSAR"
        @androidAR="onAndroidAR"
        @qrcode="onQRCode"
        @downloadDXF="onDownloadDXF"
        @cutOrder="onCutOrder"
      />
      <Footer v-show="!isBrand()" :baseUrl="baseUrl" />
      <div id="viewer">
        <canvas ref="canvas"></canvas>
        <BoundingBox
          v-show="showBoundingBox"
          :boundingBox="boundingBox"
          :corners="corners"
        />
      </div>
      <DragToMove v-show="!isBrand() && !controlled" />
      <RightsideMenu
        :userAgent="userAgent"
        :ar="true"
        :isOpenMobileMenu="isOpenMobileMenu"
        :mode="mode"
        :boundingBox="boundingBox"
        :material="material"
        :materials="materials"
        :millSize="millSize"
        :millSizes="millSizes"
        :machineType="machineType"
        :UIs="UIs()"
        :thresholds="thresholds"
        :thicknessSlider="thicknessSlider"
        @resetcamera="$options.controller.resetCamera()"
        @help="isModal = true"
        @iosar="oniOSAR"
        @androidar="onAndroidAR"
        @qrcode="onQRCode"
        @changematerial="material = $event.target.value"
        @changeMillSize="changeMillSize"
        @changeMachineType="changeMachineType"
        @syncboundingbox="syncBoundingBox"
        @regeneratetoolpath="generateToolpath"
      />
      <ShopbotMenu
        v-if="mode === Modes.TOOLPATH || mode === Modes.TOOLPATH_ONLY"
        :sbp="sbp"
        :exportComponents="exportComponents()"
        :cnc="cnc"
        @calculate="showCalculatePopup"
        @change="$options.controller.setTimeline($event)"
        @binvisible="$options.controller.setBinVisibled($event)"
        @toolpathvisible="$options.controller.setToolpathVisibled($event)"
        @labelinfovisible="$options.controller.setLabelinfoVisibled($event)"
        @cncvisible="($e) => {
          this.$options.controller.setCNCVisibled($e);
          this.cnc = $e;
        }"
        @tabvisible="$options.controller.setTabVisibled($event)"
        @bisuvisible="$options.controller.setBisuVisibled($event)"
      />
      <TemplatesPopup ref="TemplatesPopup" @selecttemplate="onSelectTemplate" />
      <UploadPopup ref="UploadPopup" @parse="parse" />
      <TermsPopup ref="TermsPopup" />
      <AlertPopup ref="AlertPopup" />
      <AmplifyLoginPopup ref="AmplifyLoginPopup" @successLogin="onSuccessLogin" />
      <LoginPopup ref="LoginPopup" @authenticated="onCutOrder" />
      <QRCodePopup ref="QRCodePopup" />
      <CalculatePopup
        ref="CalculatePopup"
        v-show="isCalculatePopup"
        :projectId="projectId"
        :contractNames="contractNames"
        :facName="factory_profile.name"
        :facAddress="factory_profile.address"
        :facInvoice="factory_setting.invoice_registration_number"
        :facPostal="factory_profile.postal_code"
        :clientAddress="clientAddress"
        :login="login"
        @updateCalculateContents="onUpdateCalculateContents"
        @updateTotalPrice="onUpdateTotalPrice"
        @closeCalculatePopup="onCloseCalculatePopup"
        @setPrice="onSetPrice"
        @updateSF="onUpdateSF"
      />
      <SetPricePopup
        class="price-popup"
        ref="SetPricePopup"
        v-if="isSetPricePopup"
        :editCard="isEditProfile"
        :manufacturing_costs="manufacturing_costs"
        :factory_setting="factory_setting"
        :factory_profile="factory_profile"
        :factory_logo="factory_logo"
        :factory_pics="factory_pics"
        :materials36="materials36"
        :materials48="materials48"
        @editCard="onEditProfile"
        @cancelEditCard="onCancelEditProfile"
        @materialUpdate="onMaterialUpdate"
        @closeSetPricePopup="onCloseSetPricePopup"
        @saveSetting="onSaveSetting"
        @saveFactoryProfile="onSaveFactoryProfile"
      />
      <LoadingIndicator v-show="isLoading" />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import 'three/examples/js/exporters/GLTFExporter';
import 'three/examples/js/exporters/OBJExporter';
import { v4 as uuidv4 } from 'uuid';

import API, { graphqlOperation } from '@aws-amplify/api';
import { Auth, Hub, Storage } from 'aws-amplify';
import * as mutations from '../api/graphql/mutations.ts';
import * as queries from '../api/graphql/queries.ts';

import FileTypes, { detectFileType } from '../assets/javascripts/FileTypes';
import MaterialTypes from '../assets/javascripts/MaterialTypes';
import Modes from '../assets/javascripts/misc/Modes';
import SBPZip from '../assets/javascripts/sbp/SBPZip';
import Theme from '../assets/javascripts/Theme';
import CalculatePopup from '../components/CalculatePopup.vue';
import LeftMenuCard from '../components/LeftMenuCard.vue';
import SetPricePopup from '../components/SetPricePopup.vue';
import TemplatesPopup from '../components/TemplatesPopup.vue';
import UploadPopup from '../components/UploadPopup.vue';
import StartModal from '../components/viewer/StartModal';
import AppBase from './AppBase.vue';

let theme = new Theme();

export default {
  name: 'App',
  extends: AppBase,
  components: {
    TemplatesPopup,
    UploadPopup,
    StartModal,
    CalculatePopup,
    SetPricePopup,
    LeftMenuCard
  },

  data () {
    return {
      isModal: true,
      isOpenSideBar: false,
      isCalculatePopup: false,
      isSetPricePopup: false,

      isAR: false,
      glbUrl: '',
      usdzUrl: '',

      materialIndex: 0,
      materials: Object.keys(MaterialTypes).map(m => m.toLowerCase()).filter(m => m !== 'transparent'),
      millSizes: [6.35, 12.7],

      isEditProfile: false,
      manufacturing_costs: {},
      factory_logo: '',
      factory_pics: [],
      factory_profile: {},
      factory_setting: {},
      material_assortments: [],
      material_samples: [],

      totalPrice: 0,
      detailPrices: [],

      materialList: {
        s: {
          // id3: [ {}, {}, {} ]
        },
        l: {
          // id3: [ {}, {}, {} ]
        }
      },
      materials36: [],
      materials48: [],
      updatedMaterialList: [],

      projectId: 0,
      sfId:'',
      sfInputId: '',
      contractNames: {},
      clientAddress: {},
    };
  },

  computed: {
    material: {
      get () {
        return this.materials[this.materialIndex];
      },
      set (v) {
        this.materialIndex = this.materials.indexOf(v);
        this.$options.controller.materialType = MaterialTypes[v.toUpperCase()];
        this.$options.controller.update();
      }
    },

    showBoundingBox: function () {
      return !this.isBrand() &&
        this.mode !== Modes.TOOLPATH &&
        this.mode !== Modes.TOOLPATH_ONLY &&
        this.boundingBox.visible &&
        this.boundingBox.size.lengthSq() > 0
    }
  },

  async mounted () {
    if (
      this.getCookie('neverShowStartModal') == "true"
      || this.$route.query.sbp
    ) {
      this.isModal = false;
    } else {
      this.isModal = true;
    }

    switch (this.$route.name) {
      case 'Brand':
        this.changeMode(Modes.BRAND);
        break;
    }

    await this.setup();

    await this.setupData();
  },

  methods: {
    async setup () {
      await this.setupEMaterials();
      await this.setupEPaints();
      await this.setupController(FileTypes.GHX, theme);
      window.addEventListener('resize', () => {
        this.$options.controller.resize();
      });
      this.setupPopups([
        this.$refs.TemplatesPopup,
        this.$refs.UploadPopup,
        this.$refs.TermsPopup,
        this.$refs.AlertPopup
      ]);

      this.isOpenSideBar = this.$route.query.sidebar !== 'close';

      const sbp = this.$route.query.sbp;
      if (sbp !== undefined) {
        this.changeMode(Modes.TOOLPATH_ONLY);
        this.requireAmplifyLogin().finally(() => {
          this.setupSbp(sbp);
          Hub.listen('auth', (e) => {
            // ログインが成功した場合に再度sbpのセットアップを行い、DXFダウンロードが行えるようにする
            // (元々ログインできている場合はHub.listenのcallbackは実行されない)
            const event = e.payload.event;
            if (event === 'signIn') {
              this.login = true;
              this.$refs.AlertPopup.hide();

              this.$refs.AmplifyLoginPopup.hide();
              this.setupSbp(sbp);
              this.setupData();
            }
          });
        });
      } else {
        const templateUrl = this.getTemplateUrl();
        if (templateUrl !== undefined) {
          await this.loadTemplate(templateUrl);
        }
      }
    },

    async setupData () {
      const myID = await this.getCurrentUserId();

      this.manufacturing_costs = await this.getCostSetting(myID);
      this.factory_setting = await this.getFactorySetting(myID);
      this.factory_profile = await this.getFactoryProfile(myID);

      this.factory_logo = await Storage.get(this.factory_profile.profile_icon);

      for (let pic of this.factory_profile.pictures) {
        let gotPic = await Storage.get(pic);
        this.factory_pics.push(gotPic);
      }

      this.material_assortments = await this.listMaterialAssortments(myID);

      let materials = await this.listMaterialsWithAssortments(myID);
      materials = materials.sort((a, b) => (a.name > b.name) ? -1 : 1);
      for (let material of materials) {
        material.assortments = material.assortments.sort((a, b) => (a.thickness - b.thickness));

        if (material.width === 1820 && material.height === 910) { // 36
          this.materials36.push(material);
        } else if (material.width === 2440 && material.height === 1220) { // 48
          this.materials48.push(material);
        }
      }
    },

    async getCurrentUserId () {
      const { data } = await API.graphql(graphqlOperation(queries.getCurrentUserId, {}));
      return data.getCurrentUserId;
    },

    async getCostSetting (userId) {
      const { data } = await API.graphql(graphqlOperation(queries.getCostSetting, { user_id: userId }));
      return data.getCostSetting || {};
    },

    async getFactorySetting (userId) {
      const { data } = await API.graphql(graphqlOperation(queries.getFactorySetting, { user_id: userId }));
      return data.getFactorySetting || {};
    },

    async getFactoryProfile (userId) {
      const { data } = await API.graphql(graphqlOperation(queries.getFactoryProfile, { user_id: userId }));
      return data.getFactoryProfile || {};
    },

    async listMaterialAssortments (userId) {
      const { data } = await API.graphql(graphqlOperation(queries.listMaterialAssortments, {
        input: { user_id: userId }
      }));
      return data.listMaterialAssortments || [];
    },

    onEditProfile () {
      this.isEditProfile = true;
    },

    onCancelEditProfile () {
      this.isEditProfile = false;
    },

    onRequireLogin () {
      this.requireAmplifyLogin();
    },

    onLogout () {
      Auth.signOut().then(() => {
        this.login = false;
      });
    },

    onSuccessLogin () {
      this.login = true;
      this.$refs.AlertPopup.hide();
    },

    onMaterialUpdate (updatedItem) {
      // record updated material items; use this record when "save" button is pressed
      console.log(updatedItem);

      if (this.updatedMaterialList.length != 0) {
        let checkList = this.updatedMaterialList.map((obj) =>  obj.id);

        (checkList.includes(updatedItem.id)) ?
        this.updatedMaterialList[checkList.indexOf(updatedItem.id)] = updatedItem :
        this.updatedMaterialList.push(updatedItem);
      } else {
        this.updatedMaterialList.push(updatedItem);
      }
    },

    onUpdateCalculateContents (calculateContents, tax) {
      this.detailPrices = [];
      for (let content of calculateContents) {
        this.detailPrices.push({
          name: (content.name.includes('（')) ? content.name.substring(0, content.name.indexOf('（')) : content.name,
          subName: (content.name.includes('（')) ? content.name.substring(content.name.indexOf('（')) : '',
          price: content.total_price
        });
      }
      this.detailPrices.push({
        name: '消費税',
        subName: '（10%）',
        price: tax
      });
    },

    onUpdateTotalPrice (val) {
      this.totalPrice = val;
    },

    showCalculatePopup () {
      this.isSetPricePopup = false;
      this.isCalculatePopup = true;
    },

    onCloseCalculatePopup () {
      this.isCalculatePopup = false;
    },

    onCloseSetPricePopup () {
      this.isSetPricePopup = false;
    },

    onSetPrice () {
      this.isCalculatePopup = false;
      this.isSetPricePopup = true;
    },

    connectingDB () {
      this.isLoading = true;
    },

    finishDB () {
      this.isLoading = false;
    },

    async updateCostSetting () {
      const result = await API.graphql(graphqlOperation(mutations.updateCostSetting, {
        updateCostSettingInput: {
          id: this.manufacturing_costs.id,
          cutting_cost: this.manufacturing_costs.cutting_cost,
          burr_cost: this.manufacturing_costs.burr_cost,
          burr_sanding_cost: this.manufacturing_costs.burr_sanding_cost,
          edge_chamfering_cost: this.manufacturing_costs.edge_chamfering_cost,
          r6mm_chamfering_cost: this.manufacturing_costs.r6mm_chamfering_cost,
          r9mm_chamfering_cost: this.manufacturing_costs.r9mm_chamfering_cost,
          is_burr_enabled: this.manufacturing_costs.is_burr_enabled,
          is_burr_sanding_enabled: this.manufacturing_costs.is_burr_sanding_enabled,
          is_edge_chamfering_enabled: this.manufacturing_costs.is_edge_chamfering_enabled,
          is_r6mm_chamfering_enabled: this.manufacturing_costs.is_r6mm_chamfering_enabled,
          is_r9mm_chamfering_enabled: this.manufacturing_costs.is_r9mm_chamfering_enabled
        }
      }));
      return result;
    },

    async updateFactorySetting () {
      const result = await API.graphql(graphqlOperation(mutations.updateFactorySetting, {
        updateFactorySettingInput: {
          id: this.factory_setting.id,
          user_id: this.factory_setting.user_id,
          sf_cutting_place_id: this.factory_setting.sf_cutting_place_id,
          invoice_registration_number: this.factory_setting.invoice_registration_number
        }
      }));
      return result;
    },

    async updateMaterialAssortment (assortment) {
      const result = await API.graphql(graphqlOperation(mutations.updateMaterialAssortment, {
        updateMaterialAssortmentInput: {
          id: assortment.id,
          is_available: assortment.is_available,
          unit_price: assortment.unit_price
        }
      }));
      return result;
    },

    async onSaveSetting (mode, data) {
      this.connectingDB();

      if (mode === 'price-setting') {
        this.manufacturing_costs = data;
        try {
          await this.updateCostSetting();
          alert("加工単価を保存しました。")
        } catch (error) {
          console.log(error);
          alert("加工単価の保存に失敗しました。");
        }

        try {
          for (let assortment of this.updatedMaterialList) {
            await this.updateMaterialAssortment(assortment);
          }
          if (this.updatedMaterialList.length != 0) {
            this.updatedMaterialList = [];
            alert("材料単価を保存しました。")
          }
        } catch (error) {
          console.log(error);
          alert("材料単価の保存に失敗しました。");
        }
      }
      else if (mode === 'basic-setting') {
        this.factory_setting = data;
        try {
          await this.updateFactorySetting();
          alert("基本設定を保存しました。")
        } catch (error) {
          console.log(error);
          alert("基本設定の保存に失敗しました。");
        }
      }

      this.finishDB();
    },

    async onUpdateSF (calculateContents) {
      this.connectingDB();

      let sfInput = {
        id: this.sfInputId,
        estimatedAmount: this.totalPrice,
        materialCost: 0,
        cuttingCost: 0,
        finishingCost: 0,
        chamferingCost: 0,
        packingCost: 0,
        estimatedShippingCost: 0
      };

      for (let content of calculateContents) {
        switch (content.tag) {
          case 'manufacturing_cost':
            sfInput.cuttingCost = content.total_price;
            break;
          case 'material_cost':
            sfInput.materialCost = content.total_price;
            break;
          case 'finishing_cost':
            sfInput.finishingCost = content.total_price;
            break;
          case 'chamfering_cost':
            sfInput.chamferingCost = content.total_price;
            break;
          case 'shipping_cost':
            sfInput.estimatedShippingCost = content.total_price;
            break;
          case 'packaging_cost':
            sfInput.packingCost = content.total_price;
            break;
        }
      };

      API.graphql({
        query: mutations.updateSFOpportunity,
        variables: {
          input: sfInput
        }
      }).then(result => {
        console.log('SF updated!');
        this.finishDB();
        // alert("SF 保存しました。");
      }).catch(error => {
        console.log(error);
        this.finishDB();
        alert("エラーが発生しました。");
      });
    },

    async onSaveFactoryProfile (profile) {
      this.connectingDB();

      this.factory_profile.name = (profile.name) ? profile.name : this.factory_profile.name;
      this.factory_profile.address = (profile.location) ? profile.location : this.factory_profile.address;
      this.factory_profile.website = (profile.website) ? profile.website : this.factory_profile.website;
      this.factory_profile.description = (profile.description) ? profile.description : this.factory_profile.description;
      this.factory_profile.postal_code = (profile.postal) ? profile.postal : this.factory_profile.postal_code;
      this.factory_profile.location = (profile.googleMapUrl) ? profile.googleMapUrl : this.factory_profile.location;

      // update only when profile icon is changed
      if (profile.logo) {
        console.log('updated logo: ', profile.logo);
        this.factory_profile.profile_icon = profile.logo;
        this.factory_logo = profile.logo; // make factoryCard display updated logo
        this.factory_profile.profile_icon = await this.getS3Key(this.factory_profile.profile_icon);
      }
      // const profile_icon = await this.getS3Key();

      // update only when profile pictures are changed
      if (profile.pictures) {
        // console.log('beforePic', this.factory_profile.pictures);
        this.factory_pics = profile.pictures; // make factoryCard display updated pictures
        this.factory_profile.pictures = await this.getS3Keys(profile.pictures);
        // console.log('afterPic', this.factory_profile.pictures);
      }
      // console.log('profile.pictures: ', profile.pictures);
      // const pictures = await this.uploadProfilePictures();
      // console.log('pictures: ', pictures);

      API.graphql({
        query: mutations.updateFactoryProfile,
        variables: {
          updateFactoryProfileInput: {
            id: this.factory_profile.id,
            name: this.factory_profile.name,
            postal_code: this.factory_profile.postal_code,
            address: this.factory_profile.address,
            location: this.factory_profile.location,
            website: this.factory_profile.website,
            profile_icon: this.factory_profile.profile_icon,
            description: this.factory_profile.description,
            pictures: this.factory_profile.pictures,
            // pictures: pictures
          }
        }
      }).then(result => {
        console.log('profile updated!');
        this.finishDB();
        alert("保存しました。");
        this.isEditProfile = false; // back to profileCard
      }).catch(error => {
        console.log(error);
        this.finishDB();
        alert("エラーが発生しました。");
      });
    },

    async getDummyImage () {
      const blob = await fetch("https://picsum.photos/200").then(res => res.blob());
      const dummyImage = new File([blob], "dummy.jpg", { type: "image/jpg" });
      return dummyImage;
    },

    async getS3Key(file) {
      // const profileIcon = file;
      // const profileIcon = await this.getDummyImage();
      // console.log('dummyImage: ', profileIcon);
      const id = uuidv4();
      const ext = file.split('image/')[1].split(';')[0];
      console.log('ext: ', ext); // file format -> png or jpg

      // get file data from Base64 string
      const base64Data = file.split(';base64,').pop();

      // turn Base64 into byteArray
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      // make Blob
      const blob = new Blob([byteArray], { type: `image/${ext}` });

      // make file
      const profileIcon = new File([blob], `profileIcon.${ext}`, { type: `image/${ext}` });

      const { key } = await Storage.put(`factory-profile/icon/${id}.${ext}`, profileIcon);
      return key;
    },

    async getS3Keys(files) {
      let keys = [];

      for (let picture of files) {
        // if base64 picture
        if (picture.startsWith('data:image')) {
          keys.push( await this.getS3Key(picture) );
        } else if (picture.startsWith('http')) {  // if url from aws s3 bucket
          keys.push( this.extractKeyFromS3Url(picture) );
        }
      }

      return keys;
    },

    extractKeyFromS3Url(url) {
      // parse url
      const parsedUrl = new URL(url);

      // remove the first '/'
      let path = parsedUrl.pathname.substring(1);

      // check and remove prefix（ex: 'public/'）
      const prefixToRemove = 'public/';
      if (path.startsWith(prefixToRemove)) {
        path = path.substring(prefixToRemove.length);
      }

      return path;
    },

    async uploadProfilePictures() {
      // TODO: replace with real data
      const profilePictures = [
        await this.getDummyImage(),
        await this.getDummyImage(),
      ];
      let pictures = [];
      for (let file of profilePictures) {
        const id = uuidv4();
        const ext = file.name.split('.').pop();
        const { key } = await Storage.put(`factory-profile/pictures/${id}.${ext}`, file, {
          contentType: file.type,
          level: 'public'
        });
        pictures.push(key);
      }
      return pictures;
    },

    async setupSbp (sbp) {
      this.isLoading = true;

      await this.setupController(FileTypes.PATH, theme);
      try {
        const toolPathUrl = decodeURIComponent(sbp);

        // tool path urlに紐づくproject idとpath json urlを取得
        const data = await this.getProjectId(toolPathUrl);
        let projectId, material;
        if (data) {
          projectId = data.id;
          this.projectId = data.id;
          material = data.material;
          const presignedUrl = await Storage.get(data.pathjson_url, {expires: 60});
          const { data: pathJson } = await axios.get(presignedUrl, {
            headers: { 'Content-Type': 'application/json' },
            responseType: 'json'
          });
          this.$options.controller.parse(pathJson);

          const projectFactory = await API.graphql(graphqlOperation(queries.getProjectFactory, {id: this.projectId}));
          this.sfId = projectFactory.data.getProjectFactory.sf_opportunity_id;

          const sfOpportunity = await API.graphql(graphqlOperation(queries.getSFOpportunity, {opportunity_id: this.sfId}));
          this.sfInputId = sfOpportunity.data.getSFOpportunity.id;

          this.contractNames = {
            personalName: sfOpportunity.data.getSFOpportunity.personalName,
            corporateName: sfOpportunity.data.getSFOpportunity.corporateName
          };
          this.clientAddress = {
            address: sfOpportunity.data.getSFOpportunity.address,
            name: sfOpportunity.data.getSFOpportunity.addressName,
            postal: sfOpportunity.data.getSFOpportunity.postalCode
          };
        } else {
          this.displayAlert('このツールパスデータにはIDが紐づけられていません');
        }

        const files = await this.unzip(toolPathUrl);
        this.resetSbp();

        this.$options.controller.addSbp(files);
        if (material !== undefined && material !== null) {
          this.$options.controller.applyTMaterial(material);
        }

        this.$options.controller.hideSbp();
        this.showSbp();

        this.sbp = new SBPZip((projectId !== null) ? `emarf-project-${projectId}` : `emarf`);
        this.sbp.add(toolPathUrl);
      } catch (e) {
        this.displayAlert('Failed to parse the SBP file.');
        console.warn(e);
      } finally {
        this.isLoading = false;
      }
    },

    async loadTemplate (path) {
      this.isLoading = true;
      await this.setupController(detectFileType(path), theme);
      return this.$options.controller.load(path, this.report, () => {
        this.isLoading = false;
        this.resetGUI();
      }, () => {}, () => {
        this.isLoading = false;
      });
    },

    onCloseModal (neverShowAgain) {
      if(neverShowAgain){
        document.cookie = 'neverShowStartModal=true';
      }

      this.isModal = false;
    },

    getTemplateUrl () {
      if (this.$route.params.id !== undefined) {
        if (this.$route.params.id.endsWith('.ply')) {
          return this.getStorageUrl('templates', `${this.$route.params.id}`);
        } else {
          return this.getStorageUrl('templates', `${this.$route.params.id}.ghx`);
        }
      }
      return undefined;
    },

    async getProjectId (toolPathUrl) {
      // CAUTION: cognito user pool認証(デフォルト)だと非ログインユーザではgraphqlが権限エラーになる
      const { data } = await API.graphql({
        query: queries.getProjectId,
        variables: {
          toolpath_url: toolPathUrl
        },
        authMode: 'API_KEY'
      });
      return data[`getProjectId`];
    },

    async listMaterialsWithAssortments (userId) {
      const result = await API.graphql(
        graphqlOperation(queries.listMaterialsWithAssortments, { user_id: userId }
      ));
      const materials = result.data.listMaterialsWithAssortments;
      return materials;
    },

    setupAR (glbUrl = '', usdzUrl = '') {
      this.isLoading = true;

      const modelViewer = this.$refs.ModelViewer;
      const callback = () => {
        this.isLoading = false;
        modelViewer.removeEventListener('load', callback);
        this.$nextTick(() => {
          // Force to refresh model-viewer in mobile environment.
          window.dispatchEvent(new Event('resize'));
        });
      };
      modelViewer.addEventListener('load', callback);

      this.glbUrl = glbUrl;
      this.usdzUrl = usdzUrl;
      this.$nextTick(() => {
        this.isAR = true;
        window.dispatchEvent(new Event('resize'));
      });
    },

    closeAR() {
      this.isAR = false;
      this.$nextTick(() => {
        this.$options.controller.resize();
      });
    },

    exportGLTF (transform = false) {
      this.isLoading = true;

      const exporter = new THREE.GLTFExporter();
      this.$options.controller.stopRendering();

      let container = this.$options.controller.container;
      let rotation = container.rotation.clone();

      if (transform) {
        container.rotation.set(rotation.x - Math.PI * 0.5, rotation.y, rotation.z);
      }
      container.updateWorldMatrix(true);

      return new Promise((resolve) => {
        exporter.parse(container, (gltf) => {
          this.isLoading = false;

          container.rotation.copy(rotation);
          container.updateWorldMatrix(true);
          this.$options.controller.startRendering();

          const blob = new Blob([gltf], {type: 'application/octet-binary'});
          resolve(blob);
        }, {
          onlyVisible: false,
          truncateDrawRange: false,
          binary: true,
          forceIndices: true,
          forcePowerOfTwoTextures: true,
          includeCustomExtensions: true
        });
      });
    },

    async oniOSAR () {
      const blob = await this.exportGLTF(false);
      const { data } = await this.requestAR(blob, 'https://usd-converter.emarf.co/from/glb');
      this.setupAR(data.glb, data.usdz);
    },

    async onAndroidAR () {
      const blob = await this.exportGLTF(true);
      // this.isLoading = true;
      // // アップロードするにはログインが必要
      // const res = await Storage.put('ar/emarf.glb', blob);
      // const url = await Storage.get(res.key).finally(() => {
      //   this.isLoading = false;
      // });
      const { data } = await this.requestAR(blob, 'https://usd-converter.emarf.co/from/glb');
      this.setupAR(data.glb);
    },

    requestAR (blob, url) {
      const fd = new FormData();
      fd.append('file', blob, 'emarf.glb');
      this.isLoading = true;
      return axios.post(url, fd).finally(() => {
        this.isLoading = false;
      });
    },

    onQRCode() {
      this.$refs.QRCodePopup.show();
    },

    changeMode (m) {
      this.mode = m;
      switch (m) {
        case Modes.BRAND: {
          this.boundingBox.visible = false;
          theme = new Theme(0xffffff, 0x000000);
          break;
        }
        default:
          theme = new Theme(0xffffff, 0x00939D);
          break;
      }
      if (this.$options.controller !== undefined) {
        this.$options.controller.theme = theme;
      }
    },

    onShowTemplate (e) {
      this.$refs.TemplatesPopup.show();
      this.changeMode(Modes.TEMPLATE);
      this.$refs.TemplatesPopup.$once('hide', () => {
        this.changeMode(Modes.NONE);
      });
    },

    async onSelectTemplate (template) {
      await this.loadTemplate(this.getStorageUrl('templates', template.path));
      const arr = template.path.split('.');
      if (arr.length > 0) {
        this.$router.push({ path: `/templates/${arr[0]}` });
      }
    },

    onUploadDesign () {
      this.changeMode(Modes.UPLOAD);

      if (this.$refs.TermsPopup.isChecked()) {
        this.$refs.UploadPopup.show();
        this.$refs.UploadPopup.$once('hide', () => {
          this.changeMode(Modes.NONE);
        });
      } else {
        this.$refs.TermsPopup.show();
        this.$refs.TermsPopup.$on('checked', () => {
          this.$refs.UploadPopup.show();
          this.changeMode(Modes.UPLOAD);
          this.$refs.UploadPopup.$once('hide', () => {
            this.changeMode(Modes.NONE);
          });
        });
        this.$refs.TermsPopup.$once('hide', () => {
          this.changeMode(Modes.NONE);
        });
      }
    },

    async parse (type, data, file, name) {
      await this.setupController(type);
      this.$options.controller.parse(data, this.report);
      this.resetGUI();
    },

    isBrand () {
      return this.mode === Modes.BRAND;
    },

    getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2)
      return parts.pop().split(';').shift();
    },
  }
};

</script>

<style lang="scss">
.left-menu-card{

  .detail {
      margin-top: 24px;

      .toggle {
        font-size: 12px;
        display: block;
        width: 100%;
        // border-bottom: 2px solid #00A9B5;
        border-bottom: 2px solid #1bffc6;
        padding-bottom: 6px;

        .img {
          svg {
            float: right;
            width: 12px;
            height: 12px;
          }
        }
      }

      ul li {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      margin-bottom: 48px;
    }
}
</style>



// WEBPACK FOOTER //
// src/pages/App.vue